import { MouseEvent, useCallback, useRef, useState } from 'react';

export function useZoomPan() {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({ startX: 0, startY: 0, lastX: 0, lastY: 0 });

  const isZoomed = zoomLevel > 1;

  const resetDragState = useCallback(() => {
    setIsDragging(false);
    dragStateRef.current = { startX: 0, startY: 0, lastX: 0, lastY: 0 };
  }, []);

  const onZoomIn = useCallback(() => {
    setZoomLevel((prev) => prev + 0.2);
    setIsPanning(true);
  }, []);

  const onZoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const newLevel = prev - 0.2;
      if (newLevel <= 1) {
        setIsPanning(false);
        setTranslate({ x: 0, y: 0 });
        resetDragState();
        return 1;
      }
      return newLevel;
    });
  }, [resetDragState]);

  const onReset = useCallback(() => {
    setZoomLevel(1);
    setIsPanning(false);
    setTranslate({ x: 0, y: 0 });
    resetDragState();
  }, [resetDragState]);

  const onPointer = useCallback(() => setIsPanning(false), []);
  const onPan = useCallback(() => setIsPanning((prev) => !prev), []);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isPanning) return;
      e.stopPropagation();
      setIsDragging(true);
      dragStateRef.current.startX = e.clientX - dragStateRef.current.lastX;
      dragStateRef.current.startY = e.clientY - dragStateRef.current.lastY;
    },
    [isPanning]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isPanning || !isDragging) return;
      setTranslate({
        x: e.clientX - dragStateRef.current.startX,
        y: e.clientY - dragStateRef.current.startY,
      });
    },
    [isPanning, isDragging]
  );

  const onMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!isPanning || !isDragging) return;
      setIsDragging(false);
      dragStateRef.current.lastX = e.clientX - dragStateRef.current.startX;
      dragStateRef.current.lastY = e.clientY - dragStateRef.current.startY;
    },
    [isPanning, isDragging]
  );

  return {
    zoomLevel,
    isZoomed,
    isPanning,
    isDragging,
    translate,
    onZoomIn,
    onZoomOut,
    onReset,
    onPointer,
    onPan,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
}

export default useZoomPan;
